/**
 * Who an inventory report screen, query or answer belongs to (batch 8 T14c, #3036).
 *
 * Report figures belong to ONE signed-in session of one user in one tenant, with the permissions that session
 * holds right now. The owner key carries tenant, user, session family and every permission that changes what
 * the server may answer (`inventory.read`, `reports.view_financial`, `work_orders.read`, `customers.read`), so a
 * downgrade is another owner: nothing cached for the richer one is read, shown while loading, or kept.
 *
 * Every input is a primitive selector of the real auth store (never the stable `hasPermission` function, whose
 * identity does not change when permissions do), so a change re-renders in the same pass. A token rotation
 * changes nothing here (the family is stable); a new login gets a new family and so a new owner.
 */
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';

export const REPORT_READ_PERMISSION = 'inventory.read';
export const REPORT_FINANCIAL_PERMISSION = 'reports.view_financial';
export const REPORT_BOOKING_PERMISSION = 'work_orders.read';
export const REPORT_PARTY_PERMISSION = 'customers.read';

export interface ReportsScope {
  readonly tenantId: string;
  readonly userId: string;
  readonly sessionFamilyId: string;
}

export interface ReportsAccess {
  /** Identity of the signed-in session, also while the portal is locked. `null`: nobody is signed in. */
  readonly scope: ReportsScope | null;
  /** Signed in and not locked: the only state in which anything may be read. */
  readonly active: boolean;
  readonly canRead: boolean;
  /** Cost, revenue, valuation and price-variance variants. */
  readonly canFinancial: boolean;
  /** The profitability booking filter (it probes whether a booking has costs). */
  readonly canBookingFilter: boolean;
  /** The profitability party filter. */
  readonly canPartyFilter: boolean;
}

const LEGACY_FAMILY = 'legacy-session';

type AuthSnapshot = ReturnType<typeof useAuthStore.getState>;

function build(
  tenantId: string | null,
  userId: string | null,
  family: string | null,
  authenticated: boolean,
  locked: boolean,
  holds: { read: boolean; financial: boolean; booking: boolean; party: boolean },
): ReportsAccess {
  const scope: ReportsScope | null =
    authenticated && typeof tenantId === 'string' && tenantId !== '' && typeof userId === 'string' && userId !== ''
      ? { tenantId, userId, sessionFamilyId: family ?? LEGACY_FAMILY }
      : null;
  const active = scope !== null && !locked;
  return {
    scope,
    active,
    canRead: active && holds.read,
    canFinancial: active && holds.read && holds.financial,
    canBookingFilter: active && holds.read && holds.financial && holds.booking,
    canPartyFilter: active && holds.read && holds.financial && holds.party,
  };
}

/** The access of the store as it is at this moment. Use it at the moment of a request, never a captured copy. */
export function readReportsAccess(state: AuthSnapshot = useAuthStore.getState()): ReportsAccess {
  return build(state.user?.tenantId ?? null, state.user?.id ?? null, state.sessionFamilyId, state.isAuthenticated, state.isLocked, {
    read: state.hasPermission(REPORT_READ_PERMISSION),
    financial: state.hasPermission(REPORT_FINANCIAL_PERMISSION),
    booking: state.hasPermission(REPORT_BOOKING_PERMISSION),
    party: state.hasPermission(REPORT_PARTY_PERMISSION),
  });
}

/** The reactive access: primitives in, one memoised object out. */
export function useReportsAccess(): ReportsAccess {
  const tenantId = useAuthStore((state) => state.user?.tenantId ?? null);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const family = useAuthStore((state) => state.sessionFamilyId);
  const authenticated = useAuthStore((state) => state.isAuthenticated);
  const locked = useAuthStore((state) => state.isLocked);
  const read = useAuthStore((state) => state.hasPermission(REPORT_READ_PERMISSION));
  const financial = useAuthStore((state) => state.hasPermission(REPORT_FINANCIAL_PERMISSION));
  const booking = useAuthStore((state) => state.hasPermission(REPORT_BOOKING_PERMISSION));
  const party = useAuthStore((state) => state.hasPermission(REPORT_PARTY_PERMISSION));
  return useMemo(
    () => build(tenantId, userId, family, authenticated, locked, { read, financial, booking, party }),
    [tenantId, userId, family, authenticated, locked, read, financial, booking, party],
  );
}

/** A string that is equal exactly for equal owners; `none` when nobody may read. */
export function ownerKey(access: ReportsAccess): string {
  if (access.scope === null || !access.active) return 'none';
  return JSON.stringify([
    access.scope.tenantId,
    access.scope.userId,
    access.scope.sessionFamilyId,
    access.canRead,
    access.canFinancial,
    access.canBookingFilter,
    access.canPartyFilter,
  ]);
}

/** The request was refused before it left the page: the session or its permissions changed since it was queued. */
export class OwnerChangedError extends Error {
  constructor() {
    super('The session or its permissions changed before the request was sent');
    this.name = 'OwnerChangedError';
  }
}

/**
 * Drops every query of another owner as soon as the owner changes, so a cached response of a previous session,
 * tenant or permission set is not merely unreachable but gone (a financial answer does not outlive a downgrade).
 */
export function useReportsCacheGuard(currentOwnerKey: string, allKey: readonly unknown[]): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.removeQueries({
      queryKey: allKey,
      predicate: (query) => query.queryKey[allKey.length] !== currentOwnerKey,
    });
  }, [queryClient, currentOwnerKey, allKey]);
}
